import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useNavHighlight from "../../hooks/useNavHighlight";
import usePageMeta from "../../hooks/usePageMeta";
import api from "../../api";
import LoaderBlock from "../loader/LoaderBlock";
import Pagination from "../shared/Pagination";
import InventoryLookupFilters from "./InventoryLookupFilters";
import InventoryLookupTable from "./InventoryLookupTable";

const now = new Date();

const InventoryLookup = () => {
    const [page, setPage] = useState(1);
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [scan_code, setScan_code] = useState("");
    const [description, setDescription] = useState("");
    const [department, setDepartment] = useState("");
    const [show, setShow] = useState("scanned");
    const [activity, setActivity] = useState("all");
    const [departments, setDepartments] = useState([]);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [grandSummary, setGrandSummary] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [adjustingUpc, setAdjustingUpc] = useState(null);
    const [exporting, setExporting] = useState(null);

    useNavHighlight("/inventory-lookup");
    usePageMeta("inventoryLookup");

    useEffect(() => {
        api.get("/api/v1/inventory/departments")
            .then(({ data }) => {
                if (data.success) setDepartments(data.departments || []);
            })
            .catch(() => {});
    }, []);

    const fetchLookup = useCallback(async (silent = false) => {
        if (silent) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        try {
            const params = new URLSearchParams({
                page: String(page),
                year: String(year),
                month: String(month),
                show,
                activity,
            });
            if (scan_code) params.set("scan_code", scan_code);
            if (description) params.set("description", description);
            if (department) params.set("department", department);

            const { data } = await api.get(`/api/v1/inventory/lookup?${params.toString()}`);
            if (data.success) {
                setRows(data.rows || []);
                setSummary(data.summary || null);
                setGrandSummary(data.grandSummary || null);
                setTotalPages(data.totalPages || 1);
                setTotalProducts(data.totalProducts || 0);
                setLastRefreshed(new Date());
            }
        } catch (err) {
            if (!silent) {
                toast.error(err?.response?.data?.message || "Failed to load inventory lookup");
            }
        } finally {
            if (silent) {
                setRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    }, [page, year, month, scan_code, description, department, show, activity]);

    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams({
            year: String(year),
            month: String(month),
            show,
            activity,
        });
        if (scan_code) params.set("scan_code", scan_code);
        if (description) params.set("description", description);
        if (department) params.set("department", department);
        return params;
    }, [year, month, show, activity, scan_code, description, department]);

    const exportFileName = `inventory_lookup_${year}_${String(month).padStart(2, "0")}`;

    const handleExportExcel = async () => {
        setExporting("excel");
        try {
            const response = await api.get(
                `/api/v1/inventory/export/excel?${buildFilterParams().toString()}`,
                { responseType: "blob" }
            );
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${exportFileName}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success("Excel export downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to export Excel");
        } finally {
            setExporting(null);
        }
    };

    const handleExportPdf = async () => {
        setExporting("pdf");
        try {
            const response = await api.get(
                `/api/v1/inventory/export/pdf?${buildFilterParams().toString()}`,
                { responseType: "blob" }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${exportFileName}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF export downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to export PDF");
        } finally {
            setExporting(null);
        }
    };

    const handlePrint = async () => {
        setExporting("print");
        try {
            const response = await api.get(
                `/api/v1/inventory/export/print?${buildFilterParams().toString()}`,
                { responseType: "text" }
            );
            const printWindow = window.open("", "_blank", "noopener,noreferrer");
            if (!printWindow) {
                toast.error("Pop-up blocked. Allow pop-ups to print the report.");
                return;
            }
            printWindow.document.write(response.data);
            printWindow.document.close();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to open print view");
        } finally {
            setExporting(null);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLookup(false);
        }, 350);
        return () => clearTimeout(timer);
    }, [fetchLookup]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchLookup(true);
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchLookup]);

    const handleAdjust = async (upc, adjustment_type) => {
        setAdjustingUpc(upc);
        try {
            await api.post("/api/v1/inventory/adjust", {
                upc,
                year,
                month,
                adjustment_type,
                quantity: 1,
            });
            await fetchLookup(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save adjustment");
        } finally {
            setAdjustingUpc(null);
        }
    };

    return (
        <Fragment>
            <section className="pl-[1.5vmax] pr-[2.5vmax]">
                <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                    <h2 className="text-[1.3vmax] font-bold">Inventory Lookup</h2>
                    <div className="flex-center gap-[0.8vmax] text-[1vmax]">
                        <Link to="/">Dashboard</Link>
                        <div className="w-[0.3vmax] h-[0.3vmax] rounded-[50%] bg-black" />
                        <span className="text-slate-400">Inventory Lookup</span>
                    </div>
                </div>
            </section>

            <section className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax]">
                <div className="p-[2vmax] bg-white shadow-1 rounded-2xl">
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                        <div>
                            <h2 className="text-[1.1vmax] font-bold text-slate-800">Monthly Inventory View</h2>
                            <p className="text-[13px] text-slate-500 mt-1">
                                Book Inventory = Opening + Item Purchase − Item Sold. On Hand = this month's physical scan count (± adjustments only); purchases do not increase On Hand.
                                Over/Short = On Hand − Book Inventory. Refreshes every 30 seconds.
                            </p>
                        </div>
                        {totalProducts > 0 && (
                            <span className="text-[13px] text-slate-500">
                                {totalProducts.toLocaleString()} items
                                {refreshing && (
                                    <span className="ml-2 text-blue-500">Refreshing…</span>
                                )}
                                {lastRefreshed && !refreshing && (
                                    <span className="ml-2 text-slate-400">
                                        Updated {lastRefreshed.toLocaleTimeString()}
                                    </span>
                                )}
                            </span>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={handleExportExcel}
                                disabled={!!exporting || loading || totalProducts === 0}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-[13px] font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="fa-solid fa-file-excel" />
                                {exporting === "excel" ? "Exporting…" : "Export Excel"}
                            </button>
                            <button
                                type="button"
                                onClick={handleExportPdf}
                                disabled={!!exporting || loading || totalProducts === 0}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="fa-solid fa-file-pdf" />
                                {exporting === "pdf" ? "Exporting…" : "Export PDF"}
                            </button>
                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={!!exporting || loading || totalProducts === 0}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-white text-[13px] font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="fa-solid fa-print" />
                                {exporting === "print" ? "Preparing…" : "Print"}
                            </button>
                        </div>
                    </div>

                    <InventoryLookupFilters
                        year={year}
                        setYear={setYear}
                        month={month}
                        setMonth={setMonth}
                        show={show}
                        setShow={setShow}
                        activity={activity}
                        setActivity={setActivity}
                        scan_code={scan_code}
                        setScan_code={setScan_code}
                        description={description}
                        setDescription={setDescription}
                        department={department}
                        setDepartment={setDepartment}
                        departments={departments}
                        setPage={setPage}
                    />

                    {(summary || grandSummary) && (
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
                            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                                <p className="text-[12px] uppercase tracking-wide text-blue-600 font-semibold">Page On Hand Qty</p>
                                <p className="text-[22px] font-bold text-blue-700 mt-1">{summary?.on_hand_qty ?? 0}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-[12px] uppercase tracking-wide text-slate-600 font-semibold">Page Total Cost</p>
                                <p className="text-[22px] font-bold text-slate-800 mt-1">${Number(summary?.total_cost ?? 0).toFixed(2)}</p>
                            </div>
                            <div className="rounded-xl bg-green-50 border border-green-100 p-4">
                                <p className="text-[12px] uppercase tracking-wide text-green-600 font-semibold">Page Total Retail</p>
                                <p className="text-[22px] font-bold text-green-700 mt-1">${Number(summary?.total_retail ?? 0).toFixed(2)}</p>
                            </div>
                            <div className="rounded-xl bg-blue-600 border border-blue-700 p-4 text-white">
                                <p className="text-[12px] uppercase tracking-wide text-blue-100 font-semibold">All Total Hand Qty</p>
                                <p className="text-[22px] font-bold mt-1">{grandSummary?.on_hand_qty ?? 0}</p>
                            </div>
                            <div className="rounded-xl bg-slate-700 border border-slate-800 p-4 text-white">
                                <p className="text-[12px] uppercase tracking-wide text-slate-300 font-semibold">All Total Cost</p>
                                <p className="text-[22px] font-bold mt-1">${Number(grandSummary?.total_cost ?? 0).toFixed(2)}</p>
                            </div>
                            <div className="rounded-xl bg-green-600 border border-green-700 p-4 text-white">
                                <p className="text-[12px] uppercase tracking-wide text-green-100 font-semibold">All Total Retail</p>
                                <p className="text-[22px] font-bold mt-1">${Number(grandSummary?.total_retail ?? 0).toFixed(2)}</p>
                            </div>
                        </div>
                    )}

                    <div className="mt-5">
                        {loading ? (
                            <LoaderBlock />
                        ) : (
                            <InventoryLookupTable
                                rows={rows}
                                onAdjust={handleAdjust}
                                adjustingUpc={adjustingUpc}
                            />
                        )}
                    </div>

                    <Pagination page={page} totalPages={totalPages} setPage={setPage} />
                </div>
            </section>
        </Fragment>
    );
};

export default InventoryLookup;
